import { createGlobalStyle } from 'styled-components';
import { createCssVariables, lightTheme } from './theme';

const GlobalStyles = createGlobalStyle`
  ${createCssVariables(lightTheme)}
  
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Base mobile-first styles */
  html, body {
    font-family: var(--font-family-text);
    font-size: 16px;
    color: var(--color-text-primary);
    background-color: var(--color-background-primary);
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
    width: 100%;
    max-width: 100vw;
    /* Reserve space for fixed prompt bar during scroll/anchor jumps */
    scroll-padding-bottom: var(--prompt-bar-offset, calc(1.5rem + 56px));
  }
  
  /* Responsive typography */
  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-family-heading);
    font-weight: 600;
    line-height: 1.2;
    margin-bottom: 1rem;
  }

  h1 {
    font-family: var(--font-family-display);
    font-size: 1.75rem; /* Smaller on mobile */

    @media (min-width: 768px) {
      font-size: var(--font-size-display);
    }
  }
  
  h2 {
    font-size: 1.5rem; /* Smaller on mobile */
    
    @media (min-width: 768px) {
      font-size: var(--font-size-heading);
    }
  }
  
  h3 {
    font-size: 1.25rem; /* Smaller on mobile */
    
    @media (min-width: 768px) {
      font-size: var(--font-size-title);
    }
  }
  
  h4 {
    font-size: 1.1rem; /* Smaller on mobile */
    
    @media (min-width: 768px) {
      font-size: var(--font-size-subtitle);
    }
  }
  
  p {
    margin-bottom: 1rem;
  }
  
  a {
    color: var(--color-primary);
    text-decoration: none;
    /* Enhanced touch target for mobile */
    padding: 0.25rem;
    display: inline-block;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  /* Improved form elements for mobile */
  button, input, select, textarea {
    font-family: inherit;
    font-size: 1rem;
  }
  
  /* Ensure all buttons are easy to tap on mobile */
  button {
    cursor: pointer;
    min-height: 44px;
    min-width: 44px;
    padding: 0.5rem 1rem;
  }

  /* Touch fundamentals: no gray tap flash, and no 300ms double-tap-zoom
     delay on interactive elements (pinch-zoom stays available). */
  button, a, [role="button"], input, select, textarea {
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }
  
  /* Improved focus states for accessibility */
  :focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
  
  /* Better touch target on inputs for mobile */
  input, select, textarea {
    min-height: 44px;
    padding: 0.5rem;
  }
  
  ::placeholder {
    color: var(--color-text-placeholder);
  }
  
  /* Responsive images */
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }
  
  /* Screen reader only class */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
  
  /* Responsive breakpoint variables */
  :root {
    --breakpoint-mobile: 320px;
    --breakpoint-tablet: 768px;
    --breakpoint-laptop: 1024px;
    --breakpoint-desktop: 1200px;
    /* Fixed prompt bar sizing used across landing page */
    --prompt-bar-height: 56px;
    --prompt-bar-gap: 1.5rem;
    --prompt-bar-offset: calc(var(--prompt-bar-gap) + var(--prompt-bar-height) + env(safe-area-inset-bottom, 0px));
  }
`;

export default GlobalStyles; 